<!-- touchscreen preview component -->
<template>
  <div class="card dashboard-card">
    <div class="card-header touch-header">
      <div class="touch-title">
        <h4 class="mb-0">Touchscreen Preview</h4>
        <small class="text-muted">Live touchscreen</small>
      </div>

      <div class="touch-actions">
        <button
          class="btn btn-sm btn-sr btn-sr--secondary touch-btn"
          @click="refresh"
          :disabled="isLoading || !isRevealed"
          title="Reload preview"
        >
          Refresh
        </button>

        <button
          class="btn btn-sm btn-sr btn-sr--primary touch-btn"
          @click="openNewTab"
          title="Open Touch in new tab"
        >
          Open Touch
        </button>
      </div>
    </div>

    <div class="card-body">
      <div class="device-frame">
        <!-- preview reveal overlay (loads iframe only after click) -->
        <div
          v-if="!isRevealed"
          class="reveal-overlay"
          @click="reveal"
          role="button"
          tabindex="0"
        >
          <div class="reveal-card">
            <div class="reveal-brand">SENSOREAL</div>
            <div class="reveal-title">Touch Preview</div>
            <div class="reveal-sub">
              Click to load the live touchscreen preview
            </div>
            <div class="reveal-cta">CLICK TO PREVIEW</div>
          </div>
        </div>

        <!-- loading overlay (only after reveal) -->
        <div v-if="isRevealed && isLoading" class="loading-overlay">
          <div class="spinner"></div>
          <div class="loading-text">Loading Touch…</div>
        </div>

        <!-- iframe only mounted after reveal -->
        <iframe
          v-if="isRevealed"
          class="touch-iframe"
          :src="iframeSrc"
          @load="onLoad"
          @error="onError"
          allow="fullscreen"
          loading="lazy"
          referrerpolicy="no-referrer"
        ></iframe>
      </div>

      <div v-if="isRevealed && loadError" class="text-danger mt-2">
        Couldn’t load Touch preview. Try Refresh or Open Touch.
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "DashboardTouchPreview",
  props: {
    touchUrl: { type: String, required: true }
  },
  data() {
    return {
      refreshKey: 0,
      isLoading: false,
      loadError: false,

      // only load iframe once user clicks overlay
      isRevealed: false
    };
  },
  computed: {
    iframeSrc() {
      const joiner = this.touchUrl.indexOf("?") === -1 ? "?" : "&";
      return `${this.touchUrl}${joiner}previewRefresh=${this.refreshKey}`;
    }
  },
  watch: {
    // whenever URL changes (refresh), show loader again (only if revealed)
    iframeSrc() {
      if (!this.isRevealed) return;
      this.isLoading = true;
      this.loadError = false;
    }
  },
  methods: {
    reveal() {
      if (this.isRevealed) return;
      this.isRevealed = true;
      this.isLoading = true;
      this.loadError = false;
    },

    refresh() {
      if (!this.isRevealed) return;
      this.refreshKey += 1;
    },

    openNewTab() {
      window.open(this.touchUrl, "_blank", "noopener,noreferrer");
    },

    onLoad() {
      this.isLoading = false;
      this.loadError = false;
      this.$emit("refreshed");
    },

    onError() {
      this.isLoading = false;
      this.loadError = true;
    }
  }
};
</script>

<style scoped lang="scss">
.touch-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.touch-actions {
  display: flex;
  gap: 10px;
}

.touch-btn {
  min-width: 110px;
  border-radius: 4px;
}

/* device frame */
.device-frame {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #0b0b0b;
  padding: 10px;
  height: 508.5px;
  margin-bottom: 24px;
  overflow: hidden;
  position: relative; /* for overlay */
}

.touch-iframe {
  width: 100%;
  height: 100%;
  border: 0;
  border-radius: 6px;
  background: #000;
}

/* loading overlay */
.loading-overlay {
  position: absolute;
  inset: 10px;
  border-radius: 6px;
  background: rgba(10, 10, 10, 0.65);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 5;
}

.loading-text {
  margin-top: 10px;
  color: #fff;
  font-size: 13px;
  opacity: 0.9;
}

/* loading spinner */
.spinner {
  width: 26px;
  height: 26px;
  border: 3px solid rgba(255, 255, 255, 0.25);
  border-top-color: rgba(255, 255, 255, 0.95);
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* preview overlay */
.reveal-overlay {
  position: absolute;
  inset: 10px;
  border-radius: 6px;
  z-index: 6;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  background: linear-gradient(
    135deg,
    rgba(12, 18, 28, 0.92),
    rgba(10, 16, 26, 0.92)
  );

  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.05);
}

.reveal-card {
  width: 86%;
  max-width: 360px;

  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;

  padding: 18px 18px 16px;
  border-radius: 10px;

  background: rgba(17, 24, 39, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);

  transform: translateY(0);
  transition: transform 220ms ease, box-shadow 220ms ease,
    background-color 220ms ease;

  position: relative;
}

.reveal-card:before {
  content: "";
  position: absolute;
  left: 18px;
  right: 18px;
  top: 14px;
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
  opacity: 0.9;
}

.reveal-overlay:hover .reveal-card {
  transform: translateY(-3px);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.35), 0 2px 6px rgba(0, 0, 0, 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

.reveal-brand {
  font-size: 11px;
  letter-spacing: 3px;
  color: rgba(255, 255, 255, 0.38);
}

.reveal-title {
  font-weight: 700;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.92);
  letter-spacing: 0.2px;
  margin-top: 8px;
}

.reveal-sub {
  margin-top: 6px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.62);
  line-height: 1.4;
  max-width: 260px;
}

/* CTA */
.reveal-cta {
  margin-top: 14px;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;

  padding: 8px 12px;
  border-radius: 8px;

  color: #26c281;
  border: 1px solid rgba(38, 194, 129, 0.35);
  background: rgba(38, 194, 129, 0.08);
}

.reveal-overlay:hover .reveal-cta {
  background: rgba(38, 194, 129, 0.12);
  border-color: rgba(38, 194, 129, 0.55);
  box-shadow: 0 0 0 3px rgba(38, 194, 129, 0.08);
}

@media (prefers-reduced-motion: reduce) {
  .reveal-card {
    transition: none;
  }
}
</style>
